import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import posthog from "posthog-js";
import { Cookie } from "lucide-react";

const KEY = "kidpath_cookie_consent";
const PH_KEY = process.env.REACT_APP_POSTHOG_KEY;
const PH_HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let analyticsStarted = false;

function setGaConsent(granted) {
  try {
    window.gtag?.("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
    });
  } catch (e) { if (process.env.NODE_ENV === "development") console.warn("GA consent update failed", e); }
}

export function startAnalytics() {
  setGaConsent(true);
  if (analyticsStarted || !PH_KEY) return;
  try {
    posthog.init(PH_KEY, {
      api_host: PH_HOST,
      person_profiles: "identified_only",
      autocapture: false,
      capture_pageview: true,
      disable_session_recording: true,
      persistence: "localStorage+cookie",
    });
    analyticsStarted = true;
  } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Analytics init failed", e); }
}

export function stopAnalytics() {
  setGaConsent(false);
  try { posthog.opt_out_capturing?.(); } catch (e) { if (process.env.NODE_ENV === "development") console.warn("Analytics opt-out failed", e); }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = localStorage.getItem(KEY);
    if (!choice) setVisible(true);
    else if (choice === "all") startAnalytics();
  }, []);

  const decide = (choice) => {
    localStorage.setItem(KEY, choice);
    if (choice === "all") startAnalytics();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9998] p-4 sm:p-5 animate-fade-up" data-testid="cookie-consent">
      <div className="max-w-3xl mx-auto kid-card p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 shadow-[0_20px_50px_rgba(25,55,80,.18)]">
        <div className="w-11 h-11 rounded-2xl bg-kid-skytint text-kid-teal grid place-items-center shrink-0">
          <Cookie size={22} />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-kid-navy text-base">We value your privacy</h3>
          <p className="text-sm text-kid-muted mt-1 leading-relaxed">
            KidPath uses necessary cookies to keep you signed in. With your consent, we also use
            optional analytics cookies to understand how families use KidPath and improve it. In line
            with Canadian privacy law (PIPEDA), analytics stay off until you accept.{" "}
            <Link to="/privacy" className="text-kid-teal font-semibold underline" data-testid="cookie-privacy-link">
              Read our Privacy Policy
            </Link>.
          </p>
        </div>
        <div className="flex gap-2 shrink-0 sm:flex-col lg:flex-row">
          <button onClick={() => decide("necessary")} className="kid-btn kid-btn-ghost h-11 px-5 text-sm whitespace-nowrap" data-testid="cookie-necessary">
            Necessary only
          </button>
          <button onClick={() => decide("all")} className="kid-btn kid-btn-primary h-11 px-5 text-sm whitespace-nowrap" data-testid="cookie-accept-all">
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
